<!-- 差分テキストビューコンポーネント（ターゲット語1語のみ強調） -->
<template>
  <div class="diff-text-container">
    <div class="reference-text" v-if="referenceSegments.length > 0">
      <span class="label">参照文:</span>
      <div class="reference-segments">
        <span
          v-for="segment in referenceSegments"
          :key="segment.id"
          class="reference-segment"
        >
          <span
          v-if="segment.score !== null"
          class="segment-score"
          :class="[getScoreClass(segment.score), { 'score-animated': shouldAnimate }]"
          :data-anim-key="segment.animationKey"
          >
            {{ formatScore(segment.score) }}
          </span>
          <span
            class="reference-word"
            :class="{ clickable: segment.clickable }"
            @click="onReferenceClick(segment)"
            :title="getReferenceTooltip(segment)"
          >
            {{ segment.display }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  referenceText: {
    type: String,
    required: true
  },
  recognizedWords: {
    type: Array,
    default: () => []
  },
  targetWord: {
    type: Object,
    default: null
  },
  missingWords: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['word-click'])

const normalizeToken = (token) => {
  if (!token) return ''
  return token.toLowerCase().replace(/[^a-z']+/g, '')
}

const renderTick = ref(0)
const shouldAnimate = computed(() =>
  Array.isArray(props.recognizedWords) &&
  props.recognizedWords.some(word => {
    const rawScore = word?.score ?? word?.accuracy ?? word?.AccuracyScore
    return rawScore !== null && rawScore !== undefined
  })
)

watch(
  () => [props.referenceText, props.recognizedWords, props.missingWords],
  () => {
    renderTick.value++
  },
  { deep: true }
)

const referenceSegments = computed(() => {
  const text = props.referenceText || ''
  const tokens = text.match(/\S+/g) || []

  const recognizedPool = props.recognizedWords.map((word, idx) => {
    const normalized = normalizeToken(word.expectedWord || word.word || '')
    return {
      raw: word,
      normalized,
      used: false,
      index: idx
    }
  })

  return tokens.map((token, idx) => {
    const normalized = normalizeToken(token)
    const isWord = normalized.length > 0
    let matched = null

    if (isWord) {
      matched = recognizedPool.find(entry => !entry.used && entry.normalized === normalized)
      if (!matched) {
        matched = recognizedPool.find(entry => !entry.used && normalizeToken(entry.raw.word || '') === normalized)
      }
    }

    let score = null
    let detail = null
    if (matched) {
      matched.used = true
      detail = matched.raw
      const rawScore = detail.score ?? detail.accuracy ?? detail.AccuracyScore ?? null
      score = rawScore != null ? Number(rawScore) : null
    } else if (isWord) {
      score = 0
    }

    const scoreValue = score != null ? Math.round(score) : null
    const scoreKey = scoreValue != null ? scoreValue : 'na'
    const matchKey = matched ? matched.index : 'miss'

    return {
      id: `${idx}-${token}-${scoreKey}-${matchKey}-${renderTick.value}`,
      display: token,
      normalized,
      score: scoreValue,
      animationKey: `${idx}-${scoreKey}-${matchKey}`,
      detail,
      clickable: isWord,
      isMissing: isWord && !matched
    }
  })
})

const formatScore = (score) => {
  if (score === null || score === undefined) return '-'
  return Math.round(score)
}

const getScoreClass = (score) => {
  if (score === null || score === undefined) return 'score-neutral'
  if (score >= 90) return 'score-high'
  if (score >= 70) return 'score-mid'
  return 'score-low'
}

const getReferenceTooltip = (segment) => {
  const pieces = [segment.display]
  if (segment.score !== null) {
    pieces.push(`${formatScore(segment.score)}点`)
  }
  if (segment.isMissing) {
    pieces.push('（未発話）')
  }
  return pieces.join(' / ')
}

const onReferenceClick = (segment) => {
  if (!segment.clickable) return

  if (segment.detail) {
    emit('word-click', segment.detail)
  } else {
    emit('word-click', {
      word: segment.normalized || segment.display,
      expectedWord: segment.normalized || segment.display,
      score: 0,
      accuracy: 0,
      isMissing: true
    })
  }
}
</script>

<style scoped>
.diff-text-container {
  padding: 1rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.reference-text {
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.reference-text .label {
  font-weight: bold;
  color: #6b7280;
  margin-right: 0.5rem;
}

.reference-segments {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.25rem;
  margin-left: 0.25rem;
}

.reference-segment {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.segment-score {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  line-height: 1;
}
.segment-score.score-neutral {
  color: #9ca3af;
}

.score-animated {
  animation: scoreFlash 0.35s ease-out;
}

@keyframes scoreFlash {
  0% {
    transform: translateY(4px);
    opacity: 0;
  }
  50% {
    transform: translateY(-2px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.segment-score.score-high {
  color: #16a34a;
}

.segment-score.score-mid {
  color: #f59e0b;
}

.segment-score.score-low {
  color: #ef4444;
}

.reference-word {
  user-select: none;
  transition: color 0.2s ease;
  color: #111827;
}

.reference-word.clickable {
  cursor: pointer;
}

.reference-word.clickable:hover {
  color: #3b82f6;
}

/* レスポンシブ */
@media (max-width: 768px) {
  .reference-segments {
    gap: 0.2rem;
  }
}
</style>

